import { ArrowLeft, CreditCard, Loader2, Lock } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { fetchStudent, initiatePayment, setReturnURL } from "../../api/action/user";
import { loadCartProducts } from "../../api/action/cart";

const ApplicantOrderHistory = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const queryParams = new URLSearchParams(location.search);
    const accountId = queryParams.get("accountId");
    const email = queryParams.get("email");
    const companyId = queryParams.get("companyId");
    const applicantId = queryParams.get("applicantId");
    const orderId = queryParams.get("orderId");
    const orderNumber = queryParams.get("orderNumber") || "";
    const businessdomain = queryParams.get("businessdomain") || "INSTITUTION";
    const payBalanceAmount = queryParams.get("payBalanceAmount");
    const hasBalance = payBalanceAmount === "hasBalance";

    const loading = false;
    const [alertMsg, setAlertMsg] = useState({
        open: false,
        message: "",
        severity: "error",
    });
    const [totalAmount, setTotalAmount] = useState(0);
    const [studentProfile, setStudentProfile] = useState(null);
    const [cartProducts, setCartProducts] = useState([]);
    const [showBalanceAlert, setShowBalanceAlert] = useState(false);
    const [blockNavigation, setBlockNavigation] = useState(hasBalance);
    const [showNavigationDialog, setShowNavigationDialog] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);

    // Prevent navigation when hasBalance is true (except during payment processing)
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (blockNavigation && !isProcessingPayment) {
                e.preventDefault();
                e.returnValue = "You have an outstanding balance. Please complete the payment before leaving.";
                return e.returnValue;
            }
        };

        const handlePopState = () => {
            if (blockNavigation && !isProcessingPayment) {
                window.history.pushState(null, "", window.location.href);
                setShowNavigationDialog(true);
            }
        };

        if (blockNavigation) {
            window.history.pushState(null, "", window.location.href);
            window.addEventListener("beforeunload", handleBeforeUnload);
            window.addEventListener("popstate", handlePopState);
            
            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
                window.removeEventListener("popstate", handlePopState);
            };
        }
    }, [blockNavigation, isProcessingPayment]);

    // Show balance alert on mount if hasBalance
    useEffect(() => {
        if (hasBalance) {
            setShowBalanceAlert(true);
            setBlockNavigation(true);
        }
    }, [hasBalance]);

    // Fetch student profile
    useEffect(() => {
        const fetchStudentProfile = async () => {
            if (applicantId) {
                try {
                    const response = await dispatch(fetchStudent({ primaryId: applicantId }));
                    if (response) {
                        setStudentProfile(response[0]);
                        localStorage.setItem("AssociatedApplicant", JSON.stringify(response.payload));
                        localStorage.setItem("applicantFeePayment", "true");
                        localStorage.setItem("applicantUserId", response[0]?.primaryId?.toString() || "");
                        if (orderId) {
                            localStorage.setItem("applicantOrderId", orderId.toString());
                        }
                    }
                } catch {
                    setAlertMsg({
                        open: true,
                        message: "Unable to load student profile",
                        severity: "error",
                    });
                }
            }
        };
        fetchStudentProfile();
    }, [applicantId, dispatch, orderId]);

    // Load cart products
    useEffect(() => {
        const getShoppingCartData = async () => {
            try {
                const response = await dispatch(loadCartProducts({ primaryId: orderId }));
                setCartProducts(response);
            } catch {
                setAlertMsg({
                    open: true,
                    message: "Unable to load order details",
                    severity: "error",
                });
            }
        };

        if (orderId) {
            getShoppingCartData();
        }
    }, [dispatch, orderId]);

    const userDetail = studentProfile;
    const companyState = useSelector((state) => state.companyReducer.company);
    const orderDetail = cartProducts;
    const searchParams = new URLSearchParams(window.location.search);
    const totalAmountParam = parseFloat(searchParams.get('amount')) || orderDetail?.subtotalAmount || 0;
    useEffect(() => {
        localStorage.setItem('applicantTotalAmount', totalAmountParam.toString());
    }, [totalAmountParam]);

    // Initialize total amount
    useEffect(() => {
        const amount = totalAmountParam
            ? Number(totalAmountParam)
            : orderDetail?.orderDetails?.[0]?.totalAmount
                ? Number(orderDetail.orderDetails[0].totalAmount)
                : 0;
        setTotalAmount(amount);
    }, [totalAmountParam, orderDetail]);

    // Calculate pricing
    const subtotal = totalAmount;
    const tax = 0;
    const shippingCharge = 0;
    const totalAmountToPay = subtotal + tax + shippingCharge;
    const currentPageName = location.pathname.split("/").pop();
    const countryCode = companyState?.operatingLocation?.code || "IND";
    const companyName = companyState?.name || "";

    const currencySymbol = () => countryCode === "US" ? "$" : "₹";

    const handleBackNavigation = () => {
        if (blockNavigation && !isProcessingPayment) {
            setShowNavigationDialog(true);
        } else {
            navigate(-1);
        }
    };

    const handleCheckout = async () => {
        try {
            setIsProcessingPayment(true);
            
            if (!orderId && !orderDetail?.orderDetails?.[0]?.primaryId) {
                throw new Error("Order information is missing");
            }

            const paymentData = {
                countryCode: countryCode,
                orderId: orderId ? Number(orderId) : 
                    orderDetail?.orderDetails?.[0]?.primaryId ?
                        Number(orderDetail.orderDetails[0].primaryId) : null,
                accountId: accountId ? Number(accountId) :
                    userDetail?.primaryId ?
                        Number(userDetail.primaryId) : null,
                orderNumber: orderNumber || orderDetail?.orderDetails?.[0]?.number,
                companyName: companyName,
                companyId: companyId,
                stripe: {
                    amount: Math.round(totalAmount),
                    customerName: `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`,
                    customerEmail: userDetail?.email || email,
                },
                razorPay: {
                    amount: Math.round(totalAmount),
                    customer: {
                        name: `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`,
                        contact: userDetail?.phoneNumber || "",
                        email: userDetail?.email || email,
                    },
                },
            };

            const returnURL = window.location.host;
            const protocol = window.location.protocol;
            await setReturnURL(protocol, returnURL, countryCode);

            const response = await initiatePayment(paymentData);
            
            if (!response) {
                throw new Error("No response received from payment gateway");
            }

            // Temporarily disable navigation blocking for successful payment redirect
            setBlockNavigation(false);
            const { stripeResponse, razorPayResponse } = response;
            if (stripeResponse) {
                window.location.href = stripeResponse;
            } else if (razorPayResponse) {
                window.location.href = razorPayResponse;
            } else {
                throw new Error("Payment gateway configuration error");
            }
        } catch (error) {
            setIsProcessingPayment(false);
            setAlertMsg({
                open: true,
                message: error.message || "Payment processing failed",
                severity: "error",
            });
        }
    };

    useEffect(() => {
        if (!showBalanceAlert) return undefined;

        const timer = setTimeout(() => setShowBalanceAlert(false), 6000);
        return () => clearTimeout(timer);
    }, [showBalanceAlert]);

    useEffect(() => {
        if (!alertMsg.open) return undefined;

        const timer = setTimeout(() => {
            setAlertMsg((current) => ({ ...current, open: false }));
        }, 6000);
        return () => clearTimeout(timer);
    }, [alertMsg.open]);

    const alertStyles = alertMsg.severity === "error"
        ? "border-red-200 bg-red-50 text-red-700"
        : "border-blue-200 bg-blue-50 text-blue-700";

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F4F1EB] to-[#ECE7DA] py-16 px-4 sm:px-6 lg:px-8 font-serif">
          <br />
            {showBalanceAlert && (
                <div className="fixed left-1/2 top-4 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 shadow-lg">
                    <div className="flex items-center gap-2">
                        <Lock size={18} />
                        <span>You have an outstanding balance. Please complete the payment.</span>
                    </div>
                </div>
            )}

            {/* Error Alert */}
            {alertMsg.open && (
                <div className={`fixed left-1/2 top-20 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-lg border px-4 py-3 shadow-lg ${alertStyles}`}>
                    <div className="flex items-center justify-between gap-3">
                        <span>{alertMsg.message}</span>
                        <button
                            type="button"
                            className="rounded px-2 py-1 text-sm font-semibold hover:bg-white/50"
                            onClick={() => setAlertMsg({ ...alertMsg, open: false })}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Navigation Block Dialog */}
            {showNavigationDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    <button
                        type="button"
                        className="absolute inset-0 h-full w-full bg-black/40"
                        onClick={() => setShowNavigationDialog(false)}
                        aria-label="Close payment required dialog"
                    />
                    <div
                        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="payment-required-title"
                    >
                        <div id="payment-required-title" className="border-b border-gray-200 px-6 py-4 text-xl font-bold text-[#3B4B5E]">
                            Payment Required
                        </div>
                        <div className="px-6 py-5">
                            <div className="flex items-center">
                                <Lock size={20} color="#f59e0b" className="mr-2" />
                                <span>You must complete the payment before leaving this page.</span>
                            </div>
                        </div>
                        <div className="flex justify-end border-t border-gray-200 px-6 py-4">
                            <button
                                type="button"
                                className="rounded-lg bg-[#3B9394] px-4 py-2 font-semibold text-white"
                                onClick={() => setShowNavigationDialog(false)}
                            >
                                Stay on Page
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Page Content */}
            <div className="max-w-4xl mx-auto mb-10">
                <div className="flex items-center text-gray-700 space-x-2 text-sm sm:text-base">
                    <button
                        className={`text-[#3B4B5E] hover:text-[#2C3E50] flex items-center ${blockNavigation ? 'cursor-not-allowed opacity-50' : ''}`}
                        onClick={handleBackNavigation}
                        disabled={blockNavigation && !isProcessingPayment}
                    >
                        <ArrowLeft size={18} className="mr-1" /> Back
                    </button>
                    <span className="font-semibold">{currentPageName}</span>
                </div>
            </div>

            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 transition duration-300 hover:shadow-2xl">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#3B4B5E] text-center mb-6">
                    {businessdomain === "RETAIL" ? "Order Summary" : "Payment Summary"}
                </h2>

                {/* Student Info */}
                {userDetail && (
                    <div className="bg-[#F0F4F8] p-6 rounded-lg mb-6 border border-gray-200">
                        <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Student Details</h3>
                        <ul className="text-gray-700 space-y-2">
                            <li><strong>Name:</strong> {userDetail.firstName} {userDetail.lastName}</li>
                            {userDetail.phoneNumber && <li><strong>Application No:</strong> {userDetail.primaryId}</li>}
                        </ul>
                    </div>
                )}

                {/* Amount Summary */}
                <div className="text-sm text-gray-600 border-t border-gray-200 pt-4 space-y-2">
                    <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{currencySymbol()}{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Tax:</span>
                        <span>{currencySymbol()}{tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-gray-800">
                        <span>Total:</span>
                        <span>{currencySymbol()}{totalAmountToPay.toFixed(2)}</span>
                    </div>
                </div>

                {/* Checkout Button */}
                <div className="mt-8 text-center">
                    <button
                        onClick={handleCheckout}
                        disabled={loading || isProcessingPayment}
                        className={`text-white font-semibold px-6 py-2 rounded-xl shadow flex items-center justify-center mx-auto ${loading || isProcessingPayment ? 'opacity-75' : ''}`}
                        style={{ backgroundColor: 'var(--education-secondary, #3B9394)' }}
                    >
                        {isProcessingPayment ? (
                            <>
                                <Loader2 size={20} className="mr-2 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <CreditCard size={20} className="mr-2" />
                                Proceed to Checkout
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApplicantOrderHistory;
